import React, { useState } from "react";
import {
  Avatar, Box, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem,
  Stack, TableCell, TableRow, Typography,
} from "@mui/material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { Row } from "../types";
import {
  AvailableCell, ClassCell, GradeSubjectCell, ModeLocationCell,
  ScheduleCell, StatusChip, UsageCell, stableAvatar,
} from "./rowHelpers";

type Props = {
  row: Row;
  onViewStaff:    (staffId: number) => void;
  onViewClass:    (classId: number) => void;
  onViewStudents: (classId: number) => void;
  onOpenSchedule: (classId: number) => void;
};

export default function StaffRow({
  row, onViewStaff, onViewClass, onViewStudents, onOpenSchedule,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const avatar = stableAvatar(row.staff_name || "?");

  return (
    <TableRow hover sx={{ verticalAlign: "top", "&:nth-of-type(even)": { background: "#fafafa" } }}>
      {/* Staff */}
      <TableCell>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ bgcolor: avatar.bg, color: avatar.color, width: 34, height: 34, fontSize: 13 }}>
            {avatar.initials}
          </Avatar>
          <Box minWidth={0}>
            <Link component="button" underline="hover"
              onClick={() => row.staff_id && onViewStaff(row.staff_id)}
              disabled={!row.staff_id}
              sx={{ fontWeight: 600, color: "#1b3a6b", textAlign: "left" }}>
              {row.staff_name || "—"}
            </Link>
            {row.staff_code && (
              <Typography variant="caption" color="text.secondary" display="block">
                Staff ID {row.staff_code}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>

      {/* Class */}
      <TableCell><ClassCell row={row} /></TableCell>

      {/* Grade & Subjects */}
      <TableCell><GradeSubjectCell row={row} /></TableCell>

      {/* Schedule */}
      <TableCell><ScheduleCell row={row} /></TableCell>

      {/* Mode & Location */}
      <TableCell><ModeLocationCell row={row} /></TableCell>

      {/* Students */}
      <TableCell>
        <Link component="button" underline="hover"
          onClick={() => onViewStudents(row.class_id)}
          sx={{ fontWeight: 600, color: "#1b3a6b" }}>
          {row.active_students} students
        </Link>
      </TableCell>

      {/* Capacity */}
      <TableCell>
        <Typography variant="body2">
          {row.capacity !== null ? `${row.capacity} seats` : "∞"}
        </Typography>
      </TableCell>

      {/* Available */}
      <TableCell><AvailableCell row={row} /></TableCell>

      {/* Usage */}
      <TableCell><UsageCell row={row} /></TableCell>

      {/* Status */}
      <TableCell><StatusChip row={row} /></TableCell>

      {/* Actions */}
      <TableCell align="right">
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem disabled={!row.staff_id}
            onClick={() => { setMenuAnchor(null); row.staff_id && onViewStaff(row.staff_id); }}>
            <ListItemIcon><PersonOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Staff" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onViewClass(row.class_id); }}>
            <ListItemIcon><ClassOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Class" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onViewStudents(row.class_id); }}>
            <ListItemIcon><PeopleAltOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Students" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onOpenSchedule(row.class_id); }}>
            <ListItemIcon><EventOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Open Schedule" />
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}
